import React, { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  STARTER_SWATCHES,
  THEME_PRESETS,
  DEFAULT_ACCENT_HEX,
  DEFAULT_BACKGROUND_HEX,
  DEFAULT_CARD_HEX,
  DEFAULT_FONT_SCALE,
  DEFAULT_LAYOUT,
  DEFAULT_READING_WIDTH,
  applyTheme,
  isValidHex,
  normalizeHex,
  type LayoutDensity,
} from '@/lib/theme';
import { useAmbiencePreferences } from '@/hooks/useAmbiencePreferences';
import { STYLE_META, getAmbienceEngine, isYouTubeStyle, type AmbienceStyle } from '@/lib/ambience';

// ─── Layout density options ────────────────────────────────────────────────
const LAYOUT_OPTIONS: { id: LayoutDensity; label: string; description: string }[] = [
  { id: 'minimalist', label: 'Cozy', description: 'Generous spacing for a relaxed reading experience.' },
  { id: 'comfortable', label: 'Comfortable', description: 'Balanced spacing for everyday use.' },
  { id: 'compact', label: 'Compact', description: 'Tighter spacing to fit more content on screen.' },
];

// ─── Font size options ─────────────────────────────────────────────────────
const FONT_SIZES: { label: string; scale: number; hint: string }[] = [
  { label: 'Small',   scale: 0.875, hint: '14 px' },
  { label: 'Default', scale: 1.0,   hint: '16 px' },
  { label: 'Large',   scale: 1.125, hint: '18 px' },
  { label: 'XL',      scale: 1.25,  hint: '20 px' },
];

// ─── Reading width options ─────────────────────────────────────────────────
const READING_WIDTHS: { label: string; width: number; hint: string }[] = [
  { label: 'Narrow',      width: 640,  hint: '640 px' },
  { label: 'Comfortable', width: 768,  hint: '768 px' },
  { label: 'Wide',        width: 960,  hint: '960 px' },
  { label: 'Full',        width: 1200, hint: '1200 px' },
];

// ─── Sub-components ────────────────────────────────────────────────────────

function StarterRow({ selectedHex, onPick }: { selectedHex: string; onPick: (hex: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STARTER_SWATCHES.map((s) => {
        const active = selectedHex.toLowerCase() === s.hex.toLowerCase();
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s.hex)}
            className={`group relative h-7 w-7 rounded-full border transition-all ${
              active ? 'border-foreground ring-2 ring-foreground/20' : 'border-border/60 hover:border-foreground/40'
            }`}
            style={{ backgroundColor: s.hex }}
            title={`${s.name} ${s.hex}`}
            aria-label={s.name}
          />
        );
      })}
    </div>
  );
}

function ColorWheelPicker({ color, onChange }: { color: string; onChange: (hex: string) => void }) {
  const [hexInput, setHexInput] = useState(color);

  useEffect(() => {
    setHexInput(color);
  }, [color]);

  const commitHex = () => {
    if (isValidHex(hexInput)) {
      onChange(normalizeHex(hexInput));
    } else {
      setHexInput(color);
    }
  };

  return (
    <div className="space-y-3">
      <StarterRow selectedHex={color} onPick={onChange} />
      <div className="flex flex-col items-stretch gap-3">
        <div className="rounded-md overflow-hidden border border-border/60 self-start">
          <HexColorPicker color={color} onChange={onChange} style={{ width: 220, height: 180 }} />
        </div>
        <div className="flex items-center gap-2">
          <span
            className="h-7 w-7 rounded border border-border/60 shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <Input
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onBlur={commitHex}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitHex(); } }}
            className="font-mono text-sm"
            placeholder="#1A2E4A"
          />
        </div>
      </div>
    </div>
  );
}

function LivePreview() {
  return (
    <div className="rounded-lg border border-border bg-background p-4 sticky top-4" style={{ minHeight: 240 }}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-3">Live preview</div>
      <div className="rounded-md border border-border bg-card p-3 space-y-2 shadow-sm">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">Reddit</span>
          <span>· u/sample</span>
          <span>· 2h ago</span>
        </div>
        <div className="text-sm font-medium text-card-foreground">
          A calm headline that won't pull you into doomscrolling
        </div>
        <div className="text-xs text-muted-foreground line-clamp-2">
          Body text in muted color. The card uses your card-surface color and the background uses your background color.
        </div>
        <div className="pt-1">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-primary text-primary-foreground text-xs px-2.5 py-1"
          >
            Accent button
          </button>
        </div>
      </div>
    </div>
  );
}

// Small swatch tile showing a preset's three colors.
function PresetTile({
  preset,
  active,
  onClick,
}: {
  preset: (typeof THEME_PRESETS)[number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={preset.description}
      className={`relative flex flex-col items-start rounded-lg border p-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'border-foreground shadow-sm' : 'border-border/60 hover:border-foreground/40 hover:shadow-sm'
      }`}
      style={{ background: preset.background }}
    >
      {/* Mini card preview */}
      <div
        className="w-full h-8 rounded mb-2 border border-black/10"
        style={{ background: preset.card }}
      />
      {/* Accent bar */}
      <div
        className="w-6 h-1.5 rounded-full mb-2"
        style={{ background: preset.accent }}
      />
      {/* Name */}
      <span
        className="text-xs font-medium truncate w-full"
        style={{ color: preset.background === '#FFFFFF' || preset.background === '#FAF7EF' || preset.background === '#FFFEF9' ? '#1a1a1a' : '#f5f5f5' }}
      >
        {preset.name}
      </span>
      {active && (
        <span
          className="absolute top-2 right-2 flex items-center justify-center h-5 w-5 rounded-full"
          style={{ background: preset.accent }}
        >
          <Check className="h-3 w-3" style={{ color: preset.background === '#0A0A0A' || preset.background.startsWith('#0') || preset.background.startsWith('#1') ? '#fff' : '#fff' }} />
        </span>
      )}
    </button>
  );
}

type ColorKey = 'background' | 'accent' | 'card';

// ─── Main component ────────────────────────────────────────────────────────

export const AppearanceSettings: React.FC = () => {
  const { theme, setTheme, resetTheme } = useTheme();
  const { prefs: ambience, update: updateAmbience, loading: ambienceLoading } = useAmbiencePreferences();

  const [bg, setBg] = useState(theme.theme_background ?? DEFAULT_BACKGROUND_HEX);
  const [accent, setAccent] = useState(theme.theme_accent ?? DEFAULT_ACCENT_HEX);
  const [card, setCard] = useState(theme.theme_card ?? theme.theme_background ?? DEFAULT_CARD_HEX);
  const [layout, setLayout] = useState<LayoutDensity>(theme.theme_layout ?? DEFAULT_LAYOUT);
  const [fontScale, setFontScale] = useState<number>(theme.theme_font_scale ?? DEFAULT_FONT_SCALE);
  const [readingWidth, setReadingWidth] = useState<number>(theme.theme_reading_width ?? DEFAULT_READING_WIDTH);

  const initialMount = useRef(true);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync local state when theme loads from the server.
  useEffect(() => {
    setBg(theme.theme_background ?? DEFAULT_BACKGROUND_HEX);
    setAccent(theme.theme_accent ?? DEFAULT_ACCENT_HEX);
    setCard(theme.theme_card ?? theme.theme_background ?? DEFAULT_CARD_HEX);
    setLayout(theme.theme_layout ?? DEFAULT_LAYOUT);
    setFontScale(theme.theme_font_scale ?? DEFAULT_FONT_SCALE);
    setReadingWidth(theme.theme_reading_width ?? DEFAULT_READING_WIDTH);
  }, [theme.theme_background, theme.theme_accent, theme.theme_card, theme.theme_layout, theme.theme_font_scale, theme.theme_reading_width]);

  // Apply colors instantly; debounce DB write 500 ms after last drag.
  useEffect(() => {
    applyTheme({ theme_background: bg, theme_accent: accent, theme_card: card, theme_layout: layout, theme_font_scale: fontScale, theme_reading_width: readingWidth });

    if (initialMount.current) { initialMount.current = false; return; }
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      setTheme({ theme_background: bg, theme_accent: accent, theme_card: card });
    }, 500);
    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
  }, [bg, accent, card]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyLayout = async (next: LayoutDensity) => {
    setLayout(next);
    await setTheme({ theme_layout: next });
  };

  const applyFontScale = async (scale: number) => {
    setFontScale(scale);
    await setTheme({ theme_font_scale: scale });
  };

  const applyReadingWidth = async (width: number) => {
    setReadingWidth(width);
    await setTheme({ theme_reading_width: width });
  };

  const applyPreset = async (preset: (typeof THEME_PRESETS)[number]) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    setBg(preset.background);
    setAccent(preset.accent);
    setCard(preset.card);
    await setTheme({ theme_background: preset.background, theme_accent: preset.accent, theme_card: preset.card });
  };

  const handleReset = async () => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    setBg(DEFAULT_BACKGROUND_HEX);
    setAccent(DEFAULT_ACCENT_HEX);
    setCard(DEFAULT_CARD_HEX);
    setLayout(DEFAULT_LAYOUT);
    setFontScale(DEFAULT_FONT_SCALE);
    setReadingWidth(DEFAULT_READING_WIDTH);
    await resetTheme();
  };

  const handleAmbienceToggle = async (enabled: boolean) => {
    await updateAmbience({ enabled });
    if (typeof window !== 'undefined') {
      const engine = getAmbienceEngine();
      if (enabled) await engine.play(ambience.style);
      else engine.pause();
    }
  };

  const handleAmbienceStyle = async (style: AmbienceStyle) => {
    await updateAmbience({ style });
    if (typeof window !== 'undefined' && ambience.enabled) {
      await getAmbienceEngine().play(style);
    }
  };

  const handleAmbienceVolume = (volume: number) => {
    updateAmbience({ volume });
    if (typeof window !== 'undefined') getAmbienceEngine().setVolume(volume);
  };

  // Determine which preset is currently active (all 3 colors must match).
  const activePresetId = THEME_PRESETS.find(
    (p) =>
      p.background.toLowerCase() === bg.toLowerCase() &&
      p.accent.toLowerCase() === accent.toLowerCase() &&
      p.card.toLowerCase() === card.toLowerCase(),
  )?.id ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Appearance</h2>
          <p className="text-sm text-muted-foreground">
            Personalize your reading room. Changes apply instantly and save automatically.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleReset}>
          Reset to default
        </Button>
      </div>

      {/* ── Theme presets ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Theme presets</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            One-click palettes. You can fine-tune any color below after picking a preset.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {THEME_PRESETS.map((preset) => (
              <PresetTile
                key={preset.id}
                preset={preset}
                active={activePresetId === preset.id}
                onClick={() => applyPreset(preset)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Custom colors ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Colors</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-6">
            <Tabs defaultValue="background" className="space-y-4">
              <TabsList>
                <TabsTrigger value="background">Background</TabsTrigger>
                <TabsTrigger value="accent">Accent</TabsTrigger>
                <TabsTrigger value="card">Card surface</TabsTrigger>
              </TabsList>
              <TabsContent value="background">
                <ColorWheelPicker color={bg} onChange={(hex) => setBg(normalizeHex(hex))} />
              </TabsContent>
              <TabsContent value="accent">
                <ColorWheelPicker color={accent} onChange={(hex) => setAccent(normalizeHex(hex))} />
              </TabsContent>
              <TabsContent value="card">
                <ColorWheelPicker color={card} onChange={(hex) => setCard(normalizeHex(hex))} />
              </TabsContent>
            </Tabs>
            <LivePreview />
          </div>
        </CardContent>
      </Card>

      {/* ── Font size ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Text size</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Scales all text in the app proportionally. Affects the reading experience most.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {FONT_SIZES.map((opt) => (
              <button
                key={opt.scale}
                type="button"
                onClick={() => applyFontScale(opt.scale)}
                className={`text-left rounded-md border px-3 py-3 transition-colors ${
                  fontScale === opt.scale
                    ? 'border-foreground bg-muted/40'
                    : 'border-border/60 hover:bg-muted/30'
                }`}
              >
                <div className="font-medium text-sm">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Reading width ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Reading width</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Sets the maximum width of your main content column for comfortable reading.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {READING_WIDTHS.map((opt) => (
              <button
                key={opt.width}
                type="button"
                onClick={() => applyReadingWidth(opt.width)}
                className={`text-left rounded-md border px-3 py-3 transition-colors ${
                  readingWidth === opt.width
                    ? 'border-foreground bg-muted/40'
                    : 'border-border/60 hover:bg-muted/30'
                }`}
              >
                <div className="font-medium text-sm">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Layout density ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Card density</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Controls spacing between elements in the feed and sidebar.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {LAYOUT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => applyLayout(opt.id)}
                className={`text-left rounded-md border p-3 transition-colors ${
                  layout === opt.id
                    ? 'border-foreground bg-muted/40'
                    : 'border-border/60 hover:bg-muted/30'
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{opt.description}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Dashboard ambience ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dashboard ambience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Optional ambient music that plays while you read. The floating player in the bottom-right corner has the same controls.
          </p>
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm">
              <div className="font-medium">Play music on dashboard load</div>
              <div className="text-xs text-muted-foreground">Default off.</div>
            </span>
            <Switch
              checked={ambience.enabled}
              onCheckedChange={handleAmbienceToggle}
              disabled={ambienceLoading}
            />
          </label>

          <div>
            <Label className="text-sm">Style</Label>
            <Select
              value={ambience.style}
              onValueChange={(v) => handleAmbienceStyle(v as AmbienceStyle)}
              disabled={ambienceLoading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STYLE_META) as AmbienceStyle[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STYLE_META[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isYouTubeStyle(ambience.style) ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                Streams from YouTube. Requires an internet connection and plays a small embedded video.
              </p>
            ) : ambience.style !== 'synth' && ambience.style !== 'silence' ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                Falls back to the Web Audio drone if the audio file isn't uploaded yet.
              </p>
            ) : null}
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <Label className="text-sm">Volume</Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {Math.round(ambience.volume * 100)}%
              </span>
            </div>
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={[ambience.volume]}
              onValueChange={(v) => handleAmbienceVolume(v[0] ?? 0)}
              disabled={ambienceLoading}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppearanceSettings;
